import { FullCbbLogo } from "@/assets/AiBotAssets";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Input from "./Input";
import type {
  OnboardingStep,
  OnboardingStepId,
  OnboardingValues,
  AppearancePreviewTab,
  StepAction,
  StepFieldName,
} from "../types";
import Headings from "./Headings";
import Button from "./Button";

type OnBoardingStepsWrapperProps = {
  activeStep: OnboardingStepId;
  currentStep: OnboardingStep;
  currentStepIndex: number;
  completedStepIds: OnboardingStepId[];
  isCurrentFieldInvalid: boolean;
  steps: OnboardingStep[];
  values: OnboardingValues;
  handleActionClick: (action: StepAction) => void | Promise<void>;
  handleFieldChange: (fieldName: StepFieldName, value: string) => void;
  loadingActionId?: StepAction["id"] | null;
  disabledActionIds?: StepAction["id"][];
  appearancePreviewTab: AppearancePreviewTab;
  fieldError?: string;
  onSkip?: () => void;
  inline?: boolean;
  className?: string;
  stepComponentProps?: Record<string, any>;
};

const OnBoardingStepsWrapper = ({
  activeStep,
  currentStep,
  currentStepIndex,
  completedStepIds,
  isCurrentFieldInvalid,
  steps,
  values,
  handleActionClick,
  handleFieldChange,
  loadingActionId = null,
  disabledActionIds = [],
  appearancePreviewTab,
  fieldError = "",
  onSkip,
  inline,
  className = "",
  stepComponentProps,
}: OnBoardingStepsWrapperProps) => {
  const currentField = currentStep.field;
  const CustomStep = currentStep.Component;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [hasVisibleScroll, setHasVisibleScroll] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const scrollThreshold = 8;

  useEffect(() => {
    const updateHasVisibleScroll = () => {
      const scrollContainer = scrollContainerRef.current;

      if (!scrollContainer) return;

      setHasVisibleScroll(
        scrollContainer.scrollHeight - scrollContainer.clientHeight >
          scrollThreshold,
      );
    };

    updateHasVisibleScroll();

    const resizeObserver = new ResizeObserver(updateHasVisibleScroll);
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      resizeObserver.observe(scrollContainer);

      Array.from(scrollContainer.children).forEach((child) => {
        resizeObserver.observe(child);
      });
    }

    window.addEventListener("resize", updateHasVisibleScroll);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHasVisibleScroll);
    };
    // Reset bottom-reached state whenever the step changes
    setIsScrolledToBottom(false);
  }, [currentStep.id, inline, steps.length]);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    // Consider "at bottom" when within 4px of the end
    setIsScrolledToBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 4);
  };

  const footerContent = (
    <div
      className={`flex w-full items-center gap-[12px] max-[1200px]:gap-[10px] ${
        inline ? "justify-between" : "justify-end"
      }`}
    >
      {inline && activeStep >= 2 && currentStepIndex < steps.length - 1 && (
        <div>
          <button
            type="button"
            onClick={onSkip}
            className="w-max text-[13px] leading-[110%] text-[#fb5f66] transition-colors hover:underline"
          >
            Skip To Builder
          </button>
        </div>
      )}
      <div
        className={`flex items-center gap-[12px] max-[1200px]:w-full max-[1200px]:flex-wrap max-[1200px]:justify-end ${
          inline ? "w-full justify-end" : "justify-end"
        } ${currentStep.id === 1 ? "max-[1200px]:me-[40px]" : ""}`}
      >
        {currentStep.actions.map((action) => (
          <Button
            key={action.id}
            action={action}
            isCurrentFieldInvalid={isCurrentFieldInvalid}
            handleActionClick={handleActionClick}
            stepId={currentStep.id}
            isLoading={loadingActionId === action.id}
            isDisabled={disabledActionIds.includes(action.id)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <motion.div
      className={`onboarding-steps-wrapper relative flex h-full min-h-0 w-full items-start justify-center lg:w-[50%] ${className}`}
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.38, ease: [0.42, 0, 0.58, 1] }}
    >
      <div className="onboarding-steps-container relative flex h-full min-h-0 w-full max-w-[708px] flex-col overflow-hidden bg-white max-[1200px]:max-w-full">
        {/* Bottom-scroll fade indicator */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[90px] left-0 z-[8] h-[80px] w-full transition-opacity duration-300"
          style={{
            background:
              "linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)",
            opacity: hasVisibleScroll && !isScrolledToBottom ? 1 : 0,
          }}
        />
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className={`relative flex min-h-0 w-full flex-1 flex-col overflow-auto bg-white px-[20px] pb-[20px] ${currentStep.id === 3 ? "h-full" : ""}`}
        >
          <div
            className={`onboarding-steps-header sticky top-0 z-[7] flex w-full justify-between gap-[15px] bg-white max-[1200px]:items-start ${currentStep.id === 2 ? "pb-[20px] max-[1200px]:static" : "pb-[50px]"} ${inline ? "items-start" : "items-center"}`}
          >
            {inline ? (
              <Headings
                step={currentStep}
                className="onboarding-header-headings"
              />
            ) : (
              <div className="flex items-center gap-[10px]">
                <FullCbbLogo />
              </div>
            )}
            <div className="onboarding-step-counter flex items-start justify-end gap-[20px]">
              <div className="flex flex-col items-end gap-[10px]">
                <div className="flex items-center gap-[5px]">
                  {steps.map((step) => {
                    const isActive = activeStep === step.id;
                    const isCompleted = completedStepIds.includes(step.id);

                    return (
                      <motion.span
                        key={step.id}
                        className="onboarding-step-indicator relative h-[5px] overflow-hidden rounded-[999px] bg-[#d9d9d9]"
                        layout
                        animate={{
                          width: isActive ? 34 : 24,
                          opacity: isActive || isCompleted ? 1 : 0.65,
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 520,
                          damping: 36,
                        }}
                      >
                        <motion.span
                          className={`absolute inset-0 origin-left rounded-[999px] ${
                            isCompleted ? "bg-[#FB5F66]" : "bg-[#FBD1D3]"
                          }`}
                          initial={false}
                          animate={{
                            scaleX: isCompleted ? 1 : isActive ? 0.58 : 0,
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 520,
                            damping: 34,
                          }}
                        />
                        {isActive && (
                          <motion.span
                            className="absolute top-0 h-full w-[10px] rounded-[999px] bg-white/70"
                            initial={{ x: -12, opacity: 0 }}
                            animate={{ x: 34, opacity: [0, 0.8, 0] }}
                            transition={{
                              duration: 1.35,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          />
                        )}
                      </motion.span>
                    );
                  })}
                </div>
                <span className="text-[12px] font-light leading-[110%] text-[#444]">
                  {currentStepIndex + 1} of {steps.length}
                </span>
              </div>
            </div>
          </div>

          <motion.div
            key={currentStep.id}
            className={`onboarding-step-content flex w-full flex-col gap-[30px]`}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.25, 1, 0.5, 1] }}
          >
            {!inline && <Headings step={currentStep} />}

            {currentField && (
              <label className="mb-[-10px] flex flex-col gap-[10px] text-[14px] font-medium leading-[110%] text-[#444]">
                {currentField.label}
                <Input
                  currentField={currentField}
                  values={values}
                  handleFieldChange={handleFieldChange}
                  error={fieldError}
                  onEnterPress={() => {
                    if (isCurrentFieldInvalid) return;
                    const primaryAction = currentStep.actions.find(
                      (a) => a.variant === "primary",
                    );
                    if (primaryAction) handleActionClick(primaryAction);
                  }}
                />
              </label>
            )}

            {CustomStep && (
              <CustomStep
                values={values}
                inline={inline}
                appearancePreviewTab={appearancePreviewTab}
                {...stepComponentProps}
              />
            )}
          </motion.div>
        </div>
        <motion.div
          className={`mt-auto px-[20px] pb-[20px] pt-[15px] ${
            hasVisibleScroll ? "bg-white" : ""
          }`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, duration: 0.18 }}
        >
          <div
            className={
              hasVisibleScroll
                ? "rounded-[16px] bg-white px-[16px] py-[12px] shadow-[0_-1.5px_15.75px_0_rgba(0,0,0,0.08)]"
                : ""
            }
          >
            {footerContent}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default OnBoardingStepsWrapper;
